import Phaser from 'phaser'
import { RoomMode, SpeedKey } from '@shared/protocol'
import { BLOCK_COLOR } from '@shared/constants'
import { loadSettings, saveSettings, GameSettings } from '../data/settings'
import { isTouchDevice } from '../input/TouchController'

const MODES:       RoomMode[] = ['1v1', '2v2', '3v3', '4v4', '5v5']
const SPEED_KEYS:  SpeedKey[] = ['slow', 'normal', 'fast', 'turbo']
const SPEED_NAMES             = ['SLOW', 'NORMAL', 'FAST', 'TURBO']

const API_BASE = (import.meta.env.VITE_SERVER_URL as string | undefined
  ?? 'wss://mean-bean-server.immoo360.workers.dev')
  .replace(/^wss:\/\//, 'https://')
  .replace(/^ws:\/\//, 'http://')

function randomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
}

// ─── Panel enum ────────────────────────────────────────────────────────────────
type Panel = 'main' | 'multi' | 'settings'

// ─── LobbyScene ────────────────────────────────────────────────────────────────

export class LobbyScene extends Phaser.Scene {
  // Settings
  private settings: GameSettings = loadSettings()

  // Panel groups
  private mainGroup!:     Phaser.GameObjects.Group
  private multiGroup!:    Phaser.GameObjects.Group
  private settingsGroup!: Phaser.GameObjects.Group

  // Multiplayer state
  private selectedMode  = 0
  private selectedSpeed = this.settings.defaultSpeed

  private modeTexts:  Phaser.GameObjects.Text[] = []
  private speedTexts: Phaser.GameObjects.Text[] = []

  // Quick match
  private qmPlayerId = ''
  private qmToken    = ''
  private qmInterval: ReturnType<typeof setInterval> | null = null
  private qmStatusTxt!: Phaser.GameObjects.Text
  private qmCancelBtn!: Phaser.GameObjects.Text
  private playBtn!:     Phaser.GameObjects.Text
  private quickBtn!:    Phaser.GameObjects.Text

  // HTML inputs
  private nameInput!: HTMLInputElement
  private roomInput!: HTMLInputElement

  // Settings UI references
  private volText!:   Phaser.GameObjects.Text
  private ghostText!: Phaser.GameObjects.Text
  private settingsSpeedTexts: Phaser.GameObjects.Text[] = []
  private touchText!: Phaser.GameObjects.Text

  constructor() { super({ key: 'LobbyScene' }) }

  create(): void {
    const W = this.scale.width
    const H = this.scale.height

    // ── Background ────────────────────────────────────────────────────────────
    this.add.rectangle(W / 2, H / 2, W, H, 0x07071a)
    this.startBgAnimation()

    // Center fog (improves title readability)
    const fog = this.add.graphics()
    fog.fillStyle(0x0a0a1a, 0.65)
    fog.fillRect(W / 2 - 260, 0, 520, H)

    // ── Title (always visible) ────────────────────────────────────────────────
    this.add.text(W / 2, 80, 'BEANSTORM', {
      fontFamily: 'monospace', fontSize: '44px',
      color: '#FFE566', stroke: '#000', strokeThickness: 5,
    }).setOrigin(0.5).setDepth(1)

    this.add.text(W / 2, 130, 'PUZZLE BATTLE', {
      fontFamily: 'monospace', fontSize: '16px', color: '#555577',
    }).setOrigin(0.5).setDepth(1)

    // ── HTML inputs ───────────────────────────────────────────────────────────
    this.createInputs()

    // ── Panels ────────────────────────────────────────────────────────────────
    this.mainGroup     = this.add.group()
    this.multiGroup    = this.add.group()
    this.settingsGroup = this.add.group()

    this.buildMainMenu(W, H)
    this.buildMultiPanel(W, H)
    this.buildSettingsPanel(W, H)

    // ── Common elements ───────────────────────────────────────────────────────
    const mob = isTouchDevice()
    const lbBtn = this.add.text(W - 18, H - 16, 'Leaderboard', {
      fontFamily: 'monospace', fontSize: mob ? '18px' : '13px', color: '#333355',
      padding: mob ? { x: 10, y: 6 } : undefined,
    }).setOrigin(1, 1).setInteractive().setDepth(2)
    lbBtn.on('pointerover',  () => lbBtn.setColor('#FFE566'))
    lbBtn.on('pointerout',   () => lbBtn.setColor('#333355'))
    lbBtn.on('pointerdown',  () => { this.cleanup(); this.scene.start('LeaderboardScene') })

    this.add.text(18, H - 16, 'v4.1', {
      fontFamily: 'monospace', fontSize: '11px', color: '#222244',
    }).setOrigin(0, 1).setDepth(2)

    this.events.on('shutdown', () => this.cleanup())

    this.showPanel('main')
  }

  // ─── Background animation ─────────────────────────────────────────────────

  private startBgAnimation(): void {
    // Falling blocks
    this.time.addEvent({ delay: 120, repeat: -1, callback: this.spawnBgBlock, callbackScope: this })
    // Chain bursts
    this.time.addEvent({ delay: 2200, repeat: -1, callback: this.spawnBurst, callbackScope: this })
  }

  private spawnBgBlock(): void {
    const W = this.scale.width
    const H = this.scale.height

    // Avoid the center fog area
    const side = Math.random() < 0.5 ? 'left' : 'right'
    const x = side === 'left'
      ? Math.random() * (W / 2 - 280)
      : W / 2 + 280 + Math.random() * (W / 2 - 280)

    const s       = 7 + Math.random() * 14
    const colorV  = 1 + Math.floor(Math.random() * 5)
    const color   = BLOCK_COLOR[colorV]
    const dur     = 5000 + Math.random() * 8000
    const alpha   = 0.12 + Math.random() * 0.28

    const block = this.add.rectangle(x, -s, s, s, color, alpha).setDepth(-1)

    // Occasionally pair with a partner
    if (Math.random() > 0.45) {
      const color2 = BLOCK_COLOR[1 + Math.floor(Math.random() * 5)]
      const b2 = this.add.rectangle(x, -s * 2 - 2, s, s, color2, alpha).setDepth(-1)
      this.tweens.add({ targets: b2, y: H + s, alpha: 0, duration: dur, ease: 'Linear', onComplete: () => b2.destroy() })
    }

    this.tweens.add({ targets: block, y: H + s, alpha: 0, duration: dur, ease: 'Linear', onComplete: () => block.destroy() })
  }

  private spawnBurst(): void {
    const W = this.scale.width
    const H = this.scale.height
    const side = Math.random() < 0.5 ? 'left' : 'right'
    const x = side === 'left'
      ? 60 + Math.random() * 340
      : W - 400 + Math.random() * 340
    const y    = 180 + Math.random() * (H - 300)
    const colorV = 1 + Math.floor(Math.random() * 5)
    const color  = BLOCK_COLOR[colorV]
    const n = 5 + Math.floor(Math.random() * 4)

    for (let i = 0; i < n; i++) {
      const angle = (Math.PI * 2 * i) / n + Math.random() * 0.5
      const dist  = 25 + Math.random() * 55
      const r     = 2 + Math.random() * 3
      const dot   = this.add.circle(x, y, r, color, 0.6).setDepth(-1)
      this.tweens.add({
        targets: dot,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0, scaleX: 0.1, scaleY: 0.1,
        duration: 550 + Math.random() * 350,
        ease: 'Quad.Out',
        onComplete: () => dot.destroy(),
      })
    }
  }

  // ─── Main menu ────────────────────────────────────────────────────────────

  private buildMainMenu(W: number, _H: number): void {
    const g   = this.mainGroup
    const cx  = W / 2
    const mob = isTouchDevice()

    const addBtn = (y: number, label: string, color: string, bg: string, size = '22px') => {
      const fs = mob ? `${Math.round(parseInt(size) * 1.35)}px` : size
      const t = this.add.text(cx, y, label, {
        fontFamily: 'monospace', fontSize: fs,
        color, backgroundColor: bg,
        padding: { x: mob ? 50 : 36, y: mob ? 20 : 14 },
      }).setOrigin(0.5).setInteractive().setDepth(2)
      g.add(t)
      return t
    }

    const campaign = addBtn(mob ? 250 : 272, 'CAMPAIGN', '#FFB347', '#1a0e00', '24px')
    campaign.on('pointerover',  () => campaign.setStyle({ color: '#ffe0a0' }))
    campaign.on('pointerout',   () => campaign.setStyle({ color: '#FFB347' }))
    campaign.on('pointerdown',  () => { this.cleanup(); this.scene.start('CampaignMapScene') })

    const multi = addBtn(mob ? 350 : 358, 'MULTIPLAYER', '#4CC9F0', '#001a2e', '24px')
    multi.on('pointerover',  () => multi.setStyle({ color: '#88ddff' }))
    multi.on('pointerout',   () => multi.setStyle({ color: '#4CC9F0' }))
    multi.on('pointerdown',  () => this.showPanel('multi'))

    const practice = addBtn(mob ? 448 : 440, 'PRACTICE OFFLINE', '#4ECB71', '#061a0e', '20px')
    practice.on('pointerover',  () => practice.setStyle({ color: '#88ffaa' }))
    practice.on('pointerout',   () => practice.setStyle({ color: '#4ECB71' }))
    practice.on('pointerdown',  () => { this.cleanup(); this.scene.start('GameScene') })

    const settings = addBtn(mob ? 540 : 514, 'SETTINGS', '#888899', '#0e0e18', '16px')
    settings.on('pointerover',  () => settings.setStyle({ color: '#bbbbcc' }))
    settings.on('pointerout',   () => settings.setStyle({ color: '#888899' }))
    settings.on('pointerdown',  () => this.showPanel('settings'))
  }

  // ─── Multiplayer panel ────────────────────────────────────────────────────

  private buildMultiPanel(W: number, _H: number): void {
    const g  = this.multiGroup
    const cx = W / 2
    const add = (obj: Phaser.GameObjects.GameObject) => { g.add(obj); return obj }

    // Back button
    const back = add(this.add.text(cx - 220, 168, '< BACK', {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
      padding: { x: 6, y: 3 },
    }).setInteractive().setDepth(2)) as Phaser.GameObjects.Text
    back.on('pointerover',  () => back.setColor('#aaaacc'))
    back.on('pointerout',   () => back.setColor('#555577'))
    back.on('pointerdown',  () => this.showPanel('main'))

    // ── Mode ────────────────────────────────────────────────────────────────
    add(this.add.text(cx, 190, 'MODE', {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
    }).setOrigin(0.5).setDepth(2))

    this.modeTexts = MODES.map((m, i) => {
      const x = cx + (i - 2) * 106
      const t = this.add.text(x, 218, m, {
        fontFamily: 'monospace', fontSize: '19px',
        color: i === this.selectedMode ? '#FFE566' : '#444466',
        backgroundColor: i === this.selectedMode ? '#222244' : undefined,
        padding: { x: 9, y: 4 },
      }).setOrigin(0.5).setInteractive().setDepth(2)
      t.on('pointerdown', () => {
        this.selectedMode = i
        this.modeTexts.forEach((t2, j) => {
          t2.setColor(j === i ? '#FFE566' : '#444466')
          t2.setBackgroundColor(j === i ? '#222244' : '')
        })
      })
      add(t)
      return t
    })

    // ── Speed ───────────────────────────────────────────────────────────────
    add(this.add.text(cx, 264, 'SPEED', {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
    }).setOrigin(0.5).setDepth(2))

    this.speedTexts = SPEED_KEYS.map((_, i) => {
      const x = cx + (i - 1.5) * 132
      const t = this.add.text(x, 290, SPEED_NAMES[i], {
        fontFamily: 'monospace', fontSize: '17px',
        color: i === this.selectedSpeed ? '#4CC9F0' : '#444466',
        backgroundColor: i === this.selectedSpeed ? '#001a2e' : undefined,
        padding: { x: 9, y: 4 },
      }).setOrigin(0.5).setInteractive().setDepth(2)
      t.on('pointerdown', () => {
        this.selectedSpeed = i
        this.speedTexts.forEach((t2, j) => {
          t2.setColor(j === i ? '#4CC9F0' : '#444466')
          t2.setBackgroundColor(j === i ? '#001a2e' : '')
        })
      })
      add(t)
      return t
    })

    // ── Name ────────────────────────────────────────────────────────────────
    add(this.add.text(cx, 334, 'NAME', {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
    }).setOrigin(0.5).setDepth(2))

    // ── Room code ───────────────────────────────────────────────────────────
    add(this.add.text(cx, 408, 'ROOM CODE', {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
    }).setOrigin(0.5).setDepth(2))

    // ── Buttons ─────────────────────────────────────────────────────────────
    this.playBtn = this.add.text(cx, 488, 'PRIVATE ROOM', {
      fontFamily: 'monospace', fontSize: '20px',
      color: '#4ECB71', backgroundColor: '#061a0e',
      padding: { x: 24, y: 10 },
    }).setOrigin(0.5).setInteractive().setDepth(2)
    this.playBtn.on('pointerover',  () => this.playBtn.setStyle({ color: '#88ffaa' }))
    this.playBtn.on('pointerout',   () => this.playBtn.setStyle({ color: '#4ECB71' }))
    this.playBtn.on('pointerdown',  () => this.startPrivateGame())
    add(this.playBtn)

    this.quickBtn = this.add.text(cx, 558, 'QUICK MATCH', {
      fontFamily: 'monospace', fontSize: '20px',
      color: '#4CC9F0', backgroundColor: '#001a2e',
      padding: { x: 24, y: 10 },
    }).setOrigin(0.5).setInteractive().setDepth(2)
    this.quickBtn.on('pointerover',  () => this.quickBtn.setStyle({ color: '#88ddff' }))
    this.quickBtn.on('pointerout',   () => this.quickBtn.setStyle({ color: '#4CC9F0' }))
    this.quickBtn.on('pointerdown',  () => this.startQuickMatch())
    add(this.quickBtn)

    this.qmStatusTxt = add(this.add.text(cx, 534, '', {
      fontFamily: 'monospace', fontSize: '16px', color: '#4CC9F0',
    }).setOrigin(0.5).setDepth(2).setVisible(false)) as Phaser.GameObjects.Text

    this.qmCancelBtn = add(this.add.text(cx, 570, '[CANCEL]', {
      fontFamily: 'monospace', fontSize: '13px', color: '#ff6666',
    }).setOrigin(0.5).setDepth(2).setVisible(false).setInteractive()) as Phaser.GameObjects.Text
    this.qmCancelBtn.on('pointerdown', () => this.cancelQuickMatch())
  }

  // ─── Settings panel ───────────────────────────────────────────────────────

  private buildSettingsPanel(W: number, _H: number): void {
    const g  = this.settingsGroup
    const cx = W / 2
    const s  = this.settings
    const add = (obj: Phaser.GameObjects.GameObject) => { g.add(obj); return obj }

    const label = (x: number, y: number, txt: string) => add(this.add.text(x, y, txt, {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
    }).setDepth(2))

    // Back button
    const back = add(this.add.text(cx - 220, 168, '< BACK', {
      fontFamily: 'monospace', fontSize: '13px', color: '#555577',
      padding: { x: 6, y: 3 },
    }).setInteractive().setDepth(2)) as Phaser.GameObjects.Text
    back.on('pointerover',  () => back.setColor('#aaaacc'))
    back.on('pointerout',   () => back.setColor('#555577'))
    back.on('pointerdown',  () => { saveSettings(this.settings); this.showPanel('main') })

    add(this.add.text(cx, 182, 'SETTINGS', {
      fontFamily: 'monospace', fontSize: '18px', color: '#e0e0ff',
    }).setOrigin(0.5).setDepth(2))

    // ── Player name ──────────────────────────────────────────────────────────
    label(cx, 228, 'PLAYER NAME')

    // ── Volume ───────────────────────────────────────────────────────────────
    label(cx - 120, 302, 'VOLUME')
    this.volText = add(this.add.text(cx - 10, 300, `${s.volume}%`, {
      fontFamily: 'monospace', fontSize: '18px', color: '#FFE566',
    }).setOrigin(0, 0).setDepth(2)) as Phaser.GameObjects.Text

    const volMinus = add(this.add.text(cx + 60, 302, '[-]', {
      fontFamily: 'monospace', fontSize: '14px', color: '#888899',
    }).setInteractive().setDepth(2)) as Phaser.GameObjects.Text
    volMinus.on('pointerdown', () => {
      this.settings.volume = Math.max(0, this.settings.volume - 10)
      this.volText.setText(`${this.settings.volume}%`)
    })

    const volPlus = add(this.add.text(cx + 94, 302, '[+]', {
      fontFamily: 'monospace', fontSize: '14px', color: '#888899',
    }).setInteractive().setDepth(2)) as Phaser.GameObjects.Text
    volPlus.on('pointerdown', () => {
      this.settings.volume = Math.min(100, this.settings.volume + 10)
      this.volText.setText(`${this.settings.volume}%`)
    })

    // ── Ghost piece ──────────────────────────────────────────────────────────
    label(cx - 120, 358, 'GHOST PIECE')
    this.ghostText = add(this.add.text(cx - 10, 356, s.ghostPiece ? 'ON' : 'OFF', {
      fontFamily: 'monospace', fontSize: '18px',
      color: s.ghostPiece ? '#4ECB71' : '#666688',
    }).setInteractive().setDepth(2)) as Phaser.GameObjects.Text
    this.ghostText.on('pointerdown', () => {
      this.settings.ghostPiece = !this.settings.ghostPiece
      this.ghostText.setText(this.settings.ghostPiece ? 'ON' : 'OFF')
      this.ghostText.setColor(this.settings.ghostPiece ? '#4ECB71' : '#666688')
    })

    // ── Default speed (for practice) ─────────────────────────────────────────
    label(cx - 120, 414, 'PRACTICE SPEED')
    this.settingsSpeedTexts = SPEED_KEYS.map((_, i) => {
      const x = cx - 10 + i * 72
      const t = add(this.add.text(x, 412, SPEED_NAMES[i], {
        fontFamily: 'monospace', fontSize: '14px',
        color: i === this.settings.defaultSpeed ? '#4CC9F0' : '#333355',
        backgroundColor: i === this.settings.defaultSpeed ? '#001a2e' : undefined,
        padding: { x: 6, y: 3 },
      }).setInteractive().setDepth(2)) as Phaser.GameObjects.Text
      t.on('pointerdown', () => {
        this.settings.defaultSpeed = i
        this.settingsSpeedTexts.forEach((t2, j) => {
          t2.setColor(j === i ? '#4CC9F0' : '#333355')
          t2.setBackgroundColor(j === i ? '#001a2e' : '')
        })
      })
      return t
    })

    // ── Touch controls ────────────────────────────────────────────────────────
    label(cx - 120, 468, 'TOUCH CONTROLS')
    const touchOpts = ['auto', 'on', 'off'] as const
    let touchIdx    = touchOpts.indexOf(s.touchControls)
    this.touchText  = add(this.add.text(cx - 10, 466, s.touchControls.toUpperCase(), {
      fontFamily: 'monospace', fontSize: '18px', color: '#888899',
    }).setInteractive().setDepth(2)) as Phaser.GameObjects.Text
    this.touchText.on('pointerdown', () => {
      touchIdx = (touchIdx + 1) % touchOpts.length
      this.settings.touchControls = touchOpts[touchIdx]
      this.touchText.setText(touchOpts[touchIdx].toUpperCase())
    })

    // Note
    label(cx, 526, '(settings are saved automatically on back)')
      .setOrigin?.(0.5)
  }

  // ─── Panel visibility ─────────────────────────────────────────────────────

  private showPanel(panel: Panel): void {
    const isMain  = panel === 'main'
    const isMulti = panel === 'multi'
    const isSettings = panel === 'settings'

    this.mainGroup.setVisible(isMain)
    this.multiGroup.setVisible(isMulti)
    this.settingsGroup.setVisible(isSettings)

    // HTML input visibility
    const nameVisible = isMulti || isSettings
    this.nameInput.style.display = nameVisible ? 'block' : 'none'
    this.roomInput.style.display = isMulti ? 'block' : 'none'

    if (nameVisible) {
      const nameY = isMulti ? 344 : 238
      this.positionInput(this.nameInput, nameY)
    }
    if (isMulti) {
      this.positionInput(this.roomInput, 418)
    }

    // Pre-fill name from settings
    if (nameVisible && !this.nameInput.value) {
      this.nameInput.value = this.settings.playerName
    }

    if (isSettings) {
      saveSettings(this.settings)
    }
  }

  // ─── HTML inputs ─────────────────────────────────────────────────────────

  private createInputs(): void {
    const style = {
      position: 'absolute', fontFamily: 'monospace',
      fontSize: '17px', padding: '7px 12px',
      background: '#12122a', color: '#e0e0ff',
      border: '1px solid #3a3a7a', borderRadius: '4px',
      outline: 'none', width: '260px',
      display: 'none',
      zIndex: '10',
    }

    this.nameInput = document.createElement('input')
    Object.assign(this.nameInput.style, style)
    this.nameInput.placeholder = 'Your name'
    this.nameInput.maxLength   = 20
    this.nameInput.value       = this.settings.playerName
    this.nameInput.addEventListener('input', () => {
      this.settings.playerName = this.nameInput.value.trim()
    })
    document.body.appendChild(this.nameInput)

    this.roomInput = document.createElement('input')
    Object.assign(this.roomInput.style, style)
    this.roomInput.placeholder = 'Room code (blank = new room)'
    document.body.appendChild(this.roomInput)
  }

  private positionInput(input: HTMLInputElement, gameY: number): void {
    const canvas = this.game.canvas
    const rect   = canvas.getBoundingClientRect()
    const scaleX = rect.width  / this.scale.width
    const scaleY = rect.height / this.scale.height
    const cx     = this.scale.width / 2

    input.style.left   = `${rect.left + (cx - 130) * scaleX}px`
    input.style.top    = `${rect.top  + gameY * scaleY}px`
    input.style.width  = `${260 * scaleX}px`
    input.style.height = `${36 * scaleY}px`
    input.style.fontSize = `${17 * scaleY}px`
  }

  // ─── Multiplayer ──────────────────────────────────────────────────────────

  private startPrivateGame(): void {
    const name   = this.nameInput.value.trim() || 'Player'
    const roomId = this.roomInput.value.trim()  || randomCode()
    this.saveNameToSettings(name)
    this.cleanup()
    this.scene.start('MultiplayerScene', {
      playerId:    crypto.randomUUID(),
      displayName: name,
      roomId,
      mode:        MODES[this.selectedMode],
      speed:       SPEED_KEYS[this.selectedSpeed],
      team:        'A',
    })
  }

  private startQuickMatch(): void {
    const name = this.nameInput.value.trim() || 'Player'
    this.saveNameToSettings(name)
    this.qmPlayerId = crypto.randomUUID()

    this.playBtn.setVisible(false)
    this.quickBtn.setVisible(false)
    this.qmStatusTxt.setText('Searching...').setVisible(true)
    this.qmCancelBtn.setVisible(true)

    fetch(`${API_BASE}/matchmaking/join`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        playerId: this.qmPlayerId,
        mode:     MODES[this.selectedMode],
        speed:    SPEED_KEYS[this.selectedSpeed],
      }),
    })
      .then(r => r.json() as Promise<{ token: string; position: number }>)
      .then(({ token, position }) => {
        this.qmToken = token
        this.qmStatusTxt.setText(`Searching... (queue: ${position})`)
        this.qmInterval = setInterval(() => this.pollMatch(name), 1500)
      })
      .catch(() => {
        this.qmStatusTxt.setText('Connection error')
        setTimeout(() => this.cancelQuickMatch(), 2000)
      })
  }

  private pollMatch(displayName: string): void {
    fetch(`${API_BASE}/matchmaking/status/${this.qmToken}`)
      .then(r => r.json() as Promise<{ matched: boolean; roomId?: string; team?: string }>)
      .then(data => {
        if (data.matched && data.roomId) {
          this.stopPolling()
          this.cleanup()
          this.scene.start('MultiplayerScene', {
            playerId:    this.qmPlayerId,
            displayName,
            roomId:      data.roomId,
            mode:        MODES[this.selectedMode],
            speed:       SPEED_KEYS[this.selectedSpeed],
            team:        data.team ?? 'A',
          })
        }
      })
      .catch(() => { /* keep polling */ })
  }

  private cancelQuickMatch(): void {
    this.stopPolling()
    this.playBtn.setVisible(true)
    this.quickBtn.setVisible(true)
    this.qmStatusTxt.setVisible(false)
    this.qmCancelBtn.setVisible(false)
  }

  private stopPolling(): void {
    if (this.qmInterval) { clearInterval(this.qmInterval); this.qmInterval = null }
  }

  private saveNameToSettings(name: string): void {
    this.settings.playerName = name
    saveSettings(this.settings)
  }

  private cleanup(): void {
    this.stopPolling()
    this.nameInput?.remove()
    this.roomInput?.remove()
  }
}
